import type { CSSProperties, ReactNode } from 'react';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { Text } from '@change/ds-core-ui';

export type Signer = {
	user?: { id?: string; displayName?: string; firstName?: string; lastName?: string } | null;
	createdAtRelativeLocalized: string;
};

/**
 * One full CSS loop (0 → translateX(-50%)) duration follows signer count, not strip width.
 * Linear px/s will vary with row width; loop rhythm is consistent for the same count.
 */
const SECONDS_PER_SIGNER_IN_LOOP = 10;
const MIN_LOOP_DURATION_SEC = 6;
const MAX_LOOP_DURATION_SEC = 180;

/** Each half-strip is tiled until its width is at least this multiple of the visible container. */
const MIN_HALF_STRIP_WIDTH_MULTIPLE_OF_CONTAINER = 2;
const MAX_TILES_PER_HALF = 8;

export function getSignerDisplayName(
	user?: { displayName?: string; firstName?: string; lastName?: string } | null,
	anonymousLabel = 'Anonymous',
): string {
	if (user?.firstName && user.lastName) {
		return `${user.firstName} ${user.lastName.charAt(0).toUpperCase()}`;
	}
	return user?.displayName || anonymousLabel;
}

export function getSignersCarouselLoopDurationSec(signerCount: number): number {
	if (signerCount <= 0) return MIN_LOOP_DURATION_SEC;
	const durationFromSignerCountSec = signerCount * SECONDS_PER_SIGNER_IN_LOOP;
	return Math.min(MAX_LOOP_DURATION_SEC, Math.max(MIN_LOOP_DURATION_SEC, durationFromSignerCountSec));
}

type RenderSignerItemOpts = {
	readonly qaId?: string;
	readonly tileIndex: number;
	readonly rowId: 'measure' | 'strip' | 'clone';
	readonly signerIndex: number;
	readonly ariaHidden?: boolean;
	readonly anonymousLabel?: string;
};

const renderSignerItem = (signer: Signer, renderOptions: RenderSignerItemOpts) => (
	<div
		key={`${renderOptions.rowId}-${renderOptions.tileIndex}-${renderOptions.signerIndex}-${signer.user?.id ?? 'anon'}`}
		className="shrink-0 pt-2 pr-3 pb-2 pl-3 whitespace-nowrap lg:pb-4"
		data-qa={renderOptions.qaId ? `${renderOptions.qaId}-item` : undefined}
		aria-hidden={renderOptions.ariaHidden}
	>
		<Text variant="body" size="x-small" className="text-typography-strong font-light">
			{getSignerDisplayName(signer.user, renderOptions.anonymousLabel)}
			{' • '}
			{signer.createdAtRelativeLocalized}
		</Text>
	</div>
);

type RenderTiledSignersOpts = {
	readonly tiles: number;
	readonly qaId?: string;
	readonly anonymousLabel?: string;
	readonly rowId: 'strip' | 'clone';
};

function renderTiledSigners(signersList: readonly Signer[], options: RenderTiledSignersOpts): ReactNode[] {
	const { tiles, qaId, anonymousLabel, rowId } = options;
	const out: ReactNode[] = [];
	for (let tileIndex = 0; tileIndex < tiles; tileIndex += 1) {
		const hideDuplicateTileFromA11y = rowId === 'strip' && tileIndex > 0;
		for (const [i, signer] of signersList.entries()) {
			out.push(
				renderSignerItem(signer, {
					qaId,
					anonymousLabel,
					tileIndex,
					rowId,
					signerIndex: i,
					ariaHidden: hideDuplicateTileFromA11y,
				}),
			);
		}
	}
	return out;
}

export function getSignersCarouselTilesPerHalf(params: {
	readonly containerWidth: number;
	readonly onePassWidth: number;
}): number {
	const { containerWidth, onePassWidth } = params;
	if (containerWidth <= 0 || onePassWidth <= 0) return 1;
	const minHalfWidth = MIN_HALF_STRIP_WIDTH_MULTIPLE_OF_CONTAINER * containerWidth;
	return Math.min(MAX_TILES_PER_HALF, Math.max(1, Math.ceil(minHalfWidth / onePassWidth)));
}

export type SignersCarouselProps = {
	signers: readonly Signer[];
	testId?: string;
	qaId?: string;
	anonymousLabel?: string;
};

export const SignersCarousel: React.FC<SignersCarouselProps> = ({ signers, testId, qaId, anonymousLabel }) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const onePassMeasureRef = useRef<HTMLDivElement>(null);
	const [tilesPerHalf, setTilesPerHalf] = useState(1);
	const durationSec = useMemo(() => getSignersCarouselLoopDurationSec(signers.length), [signers.length]);

	useLayoutEffect(() => {
		const container = containerRef.current;
		const onePass = onePassMeasureRef.current;
		if (!container || !onePass || typeof ResizeObserver === 'undefined') {
			return undefined;
		}

		const updateTiles = () => {
			const containerWidth = container.clientWidth;
			const onePassWidth = onePass.scrollWidth;
			setTilesPerHalf(getSignersCarouselTilesPerHalf({ containerWidth, onePassWidth }));
		};

		const resizeObserver = new ResizeObserver(updateTiles);
		resizeObserver.observe(container);
		resizeObserver.observe(onePass);
		return () => {
			resizeObserver.disconnect();
		};
	}, [signers]);

	if (signers.length === 0) {
		return (
			<div className="flex h-4 w-full flex-row items-start overflow-x-hidden" data-testid={testId} data-qa={qaId} />
		);
	}

	return (
		<div
			ref={containerRef}
			className="group relative flex w-full max-w-full min-w-0 flex-row items-start overflow-x-hidden"
			data-testid={testId}
			data-qa={qaId}
		>
			<div
				ref={onePassMeasureRef}
				className="pointer-events-none fixed top-0 left-0 z-[-1] flex w-max shrink-0 flex-row items-start opacity-0"
				aria-hidden
			>
				{signers.map((signer, index) =>
					renderSignerItem(signer, {
						tileIndex: 0,
						rowId: 'measure',
						signerIndex: index,
						anonymousLabel,
					}),
				)}
			</div>
			<div
				className="animate-signers-carousel group-hover:paused flex shrink-0 flex-row items-start"
				style={
					{
						width: 'max-content',
						'--signers-carousel-duration': `${durationSec}s`,
					} as CSSProperties
				}
			>
				<div className="flex shrink-0 flex-row items-start">
					{renderTiledSigners(signers, {
						tiles: tilesPerHalf,
						qaId,
						anonymousLabel,
						rowId: 'strip',
					})}
				</div>
				<div aria-hidden="true" className="flex shrink-0 flex-row items-start">
					{renderTiledSigners(signers, {
						tiles: tilesPerHalf,
						qaId,
						anonymousLabel,
						rowId: 'clone',
					})}
				</div>
			</div>
		</div>
	);
};
